import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CONFIG, { getUserCity, isServiceEnabled, fetchRemoteConfig, getServiceFees } from "../../config";
import GoogleMapPreview from "../common/GoogleMapPreview";
import CurrentLocationDetector from "../common/CurrentLocationDetector";
import CurrentLocationCard from "../common/CurrentLocationCard";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const STORAGE_KEY  = "cartItems";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

// ─────────────────────────────────────────────────────────────────────────────
// AUTH
// ─────────────────────────────────────────────────────────────────────────────
const getLoggedInUser = () => {
  try { return JSON.parse(localStorage.getItem("currentUser") || "null"); }
  catch { return null; }
};

const getAuthToken = () =>
  localStorage.getItem("authToken")    ||
  localStorage.getItem("accessToken")  ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("token") || "";

const authHeaders = () => ({
  "Content-Type": "application/json",
  ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
});

// ─────────────────────────────────────────────────────────────────────────────
// CART — same normaliser/resolve contract as ScheduleSummaryPage.js, so both
// pages read/write the exact same localStorage("cartItems") shape.
// ─────────────────────────────────────────────────────────────────────────────
const normalizeItem = (item, index) => {
  if (!item || typeof item !== "object") return null;
  const id = item.id ?? item.service_id ?? `item-${index}`;
  if (!id) return null;
  return {
    id:            String(id),
    dbId:          item.dbId || String(item._id || id),
    title:         item.title    || item.service_name || item.name || "Service",
    name:          item.name     || item.title        || item.service_name || "Service",
    short:         item.short    || item.subtitle     || item.category || "",
    subtitle:      item.subtitle || item.short        || item.category || "",
    price:         Number(item.price  || item.amount  || 0),
    image:         item.image    || null,
    category:      item.category || "",
    qty:           Math.max(1, Number(item.qty || 1)),
  };
};

const normalizeCart = (raw) => {
  if (!Array.isArray(raw)) return [];
  const map = new Map();
  raw.forEach((item, i) => {
    const n = normalizeItem(item, i);
    if (!n) return;
    if (map.has(n.id)) {
      const existing = map.get(n.id);
      if (n.qty > existing.qty) map.set(n.id, n);
    } else {
      map.set(n.id, n);
    }
  });
  return Array.from(map.values());
};

const readLocalCart = () => {
  try { return normalizeCart(JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]")); }
  catch { return []; }
};

const clearLocalCart = () => {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event("cart-updated"));
};

const resolveCart = (locationState) => {
  if (Array.isArray(locationState?.cartItems) && locationState.cartItems.length > 0) {
    const fromState = normalizeCart(locationState.cartItems);
    if (fromState.length > 0) return fromState;
  }
  return readLocalCart();
};

const apiCartRemoveItem = async (mobile, item) => {
  if (!mobile || !item) return;
  try {
    const url = item.dbId
      ? `${API_BASE_URL}/api/user/user-cart/${encodeURIComponent(mobile)}/remove/${encodeURIComponent(item.dbId)}/`
      : `${API_BASE_URL}/api/user/user-cart/${encodeURIComponent(mobile)}/remove-by-service/${encodeURIComponent(String(item.id))}/`;
    await fetch(url, { method:"DELETE", credentials:"include", headers:authHeaders() });
  } catch (e) { console.warn("[Cart] remove error:", e.message); }
};

const apiCartClear = async (mobile) => {
  if (!mobile) return;
  try {
    await fetch(`${API_BASE_URL}/api/user/user-cart/${encodeURIComponent(mobile)}/clear/`, {
      method:"DELETE", credentials:"include", headers:authHeaders(),
    });
  } catch (e) { console.warn("[Cart] clear error:", e.message); }
};

const apiPlaceOrder = async (payload) => {
  const res  = await fetch(`${API_BASE_URL}/api/user/place-order/`, {
    method:"POST", credentials:"include", headers:authHeaders(), body:JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok || !data?.success) {
    const err = new Error(data?.message || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
};

const fmt = (v) => `₹${Number(v||0).toLocaleString("en-IN")}`;
const ADDR_LABEL_ICONS = { Home: "🏠", Work: "🏢", Other: "📍" };

// ─────────────────────────────────────────────────────────────────────────────
// "Book a slot later today" — reuses the same generic (duration-omitted)
// booking_slots/seat-availability system Schedule Booking uses, scoped to
// today only (includeToday:true — see labour_service/views.py slot_availability)
// and gated to slots at least 1 hour from now (also enforced server-side in
// place_order() — this is a UX filter, not the only guard).
// ─────────────────────────────────────────────────────────────────────────────
const formatTodayDDMMYYYY = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2,"0")}-${String(d.getMonth()+1).padStart(2,"0")}-${d.getFullYear()}`;
};

const parseSlotClockTime = (timeStr) => {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(String(timeStr||"").trim());
  if (!m) return null;
  let h = parseInt(m[1],10);
  const min = parseInt(m[2],10);
  const ap = m[3].toUpperCase();
  if (ap === "PM" && h !== 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return { h, min };
};

const slotTimeToday = (timeStr) => {
  const parsed = parseSlotClockTime(timeStr);
  if (!parsed) return null;
  const d = new Date();
  d.setHours(parsed.h, parsed.min, 0, 0);
  return d;
};

// A today-slot is pickable only if it has open seats AND is more than 1
// hour from right now.
const isTodaySlotSelectable = (slot) => {
  const hasSeats = Boolean(slot?.availability) && Number(slot?.seats||0) - Number(slot?.booked||0) > 0;
  if (!hasSeats) return false;
  const dt = slotTimeToday(slot.time);
  if (!dt) return false;
  return dt.getTime() > Date.now() + 60*60*1000;
};

// ─────────────────────────────────────────────────────────────────────────────
// CURRENT LOCATION (Google Maps) — same contract as ScheduleSummaryPage.js.
// While GOOGLE_MAPS_API_KEY is blank in config.js, this runs in Testing Mode
// (pick a dummy location); a real key switches it to live GPS + reverse
// geocoding automatically.
// ─────────────────────────────────────────────────────────────────────────────
const GMAPS_KEY            = CONFIG?.GOOGLE_MAPS_API_KEY || "";
const IS_GOOGLE_CONFIGURED = Boolean(GMAPS_KEY);

const DUMMY_TEST_LOCATIONS = [
  { id: "test-1", label: "Current Location", line1: "MG Road",          area: "MG Road",          city: "Bengaluru", state: "Karnataka", pincode: "560001", lat: 12.9757, lng: 77.6069 },
  { id: "test-2", label: "Current Location", line1: "Devaraja Mohalla", area: "Devaraja Mohalla", city: "Mysuru",     state: "Karnataka", pincode: "570001", lat: 12.3052, lng: 76.6552 },
  { id: "test-3", label: "Current Location", line1: "Whitefield",       area: "Whitefield",        city: "Bengaluru", state: "Karnataka", pincode: "560066", lat: 12.9698, lng: 77.7500 },
];


const SectionTitle = ({ title, desc, right }) => (
  <div style={S.sectionHead}>
    <div><h3 style={S.cardTitle}>{title}</h3>{desc && <p style={S.cardDesc}>{desc}</p>}</div>
    {right}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// INSTANT CHECKOUT — no slot picker, no payment-at-checkout. Places the order
// unpaid (booking_type:"instant") and redirects to /instant-tracking/:orderId
// where searching → arrival → payment → completion → confirmation all happen.
// ─────────────────────────────────────────────────────────────────────────────
const InstantSummaryPageContent = () => {
  const location = useLocation();
  const navigate  = useNavigate();

  const loggedInUser = useMemo(() => getLoggedInUser(), []);
  const userName     = loggedInUser?.name || loggedInUser?.full_name || "Guest";
  const userMobile   = loggedInUser?.mobile || loggedInUser?.phone || location.state?.user?.mobile || "";
  const addressKey   = loggedInUser?.id || loggedInUser?.user_id || userMobile;

  const [cartItems, setCartItemsRaw] = useState(() => resolveCart(location.state));
  const setCartItems = (updater) => {
    setCartItemsRaw((prev) => {
      const next = normalizeCart(typeof updater === "function" ? updater(prev) : updater);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      window.dispatchEvent(new Event("cart-updated"));
      return next;
    });
  };

  useEffect(() => {
    const handler = () => setCartItemsRaw(readLocalCart());
    window.addEventListener("storage",      handler);
    window.addEventListener("cart-updated", handler);
    return () => {
      window.removeEventListener("storage",      handler);
      window.removeEventListener("cart-updated", handler);
    };
  }, []);

  const [instructions,  setInstructions]  = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [placing,       setPlacing]       = useState(false);
  const [placeError,    setPlaceError]    = useState("");

  // ── Timing: dispatch right now, or book a today slot 1hr+ from now ────────
  // Duration (1hr/2hr) mirrors Schedule Booking's Home Cleaning duration
  // choice — it selects which seat template/slot grid gets queried, same
  // as ScheduleBookingPage.js's DURATIONS picker.
  const [timingMode,       setTimingMode]       = useState("now"); // "now" | "slot"
  const [selectedDuration, setSelectedDuration] = useState(1);     // 1 | 2 (hours)
  const [todaySlots,       setTodaySlots]       = useState([]);
  const [slotLoading,      setSlotLoading]      = useState(false);
  const [slotError,        setSlotError]        = useState("");
  const [selectedSlot,     setSelectedSlot]     = useState(null); // { time, slotId }

  const fetchTodaySlots = async (duration) => {
    if (!cartItems.length) return;
    setSlotLoading(true);
    setSlotError("");
    setSelectedSlot(null);
    try {
      const serviceId = String(cartItems[0].id);
      const res  = await fetch(`${API_BASE_URL}/api/booking/slot-availability`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ serviceId, duration, includeToday: true, daysAhead: 1 }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.message || "Unable to load slot availability.");
      const days     = data?.data?.days || [];
      const todayDay = days.find((d) => d.date === formatTodayDDMMYYYY());
      setTodaySlots(todayDay?.slots || []);
    } catch (e) {
      setSlotError(e.message || "Unable to load slot availability.");
    } finally {
      setSlotLoading(false);
    }
  };

  const handleChooseTimingMode = (mode) => {
    setTimingMode(mode);
    if (mode === "slot") fetchTodaySlots(selectedDuration);
  };

  const handleChooseDuration = (duration) => {
    setSelectedDuration(duration);
    fetchTodaySlots(duration);
  };

  // ── Saved addresses ───────────────────────────────────────────────────────
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [addrLoading,    setAddrLoading]    = useState(false);
  const [selectedAddrId, setSelectedAddrId] = useState("");
  const selectedAddress = useMemo(
    () => savedAddresses.find(a => a.id === selectedAddrId) || null,
    [savedAddresses, selectedAddrId]
  );

  useEffect(() => {
    if (!addressKey) return;
    setAddrLoading(true);
    fetch(`${API_BASE_URL}/api/user/user-addresses/${encodeURIComponent(addressKey)}/`, {
      credentials: "include", headers: authHeaders(),
    })
      .then(r => r.json())
      .then(data => {
        const addrs = Array.isArray(data?.data) ? data.data : [];
        setSavedAddresses(addrs);
        if (addrs.length) setSelectedAddrId(addrs[0].id);
      })
      .catch(e => console.warn("[Addresses] fetch error:", e.message))
      .finally(() => setAddrLoading(false));
  }, [addressKey]);

  // ── Current Location (Google API when configured, else Testing Mode) ───────
  const [locationMode,        setLocationMode]        = useState("saved"); // "saved" | "current"
  const [currentLocationAddr, setCurrentLocationAddr]  = useState(null);
  const [testLocationId,      setTestLocationId]       = useState("");

  const handleSelectTestLocation = (id) => {
    setTestLocationId(id);
    setCurrentLocationAddr(DUMMY_TEST_LOCATIONS.find(l => l.id === id) || null);
  };

  const effectiveAddress = locationMode === "current" ? currentLocationAddr : selectedAddress;

  // ── Totals ───────────────────────────────────────────────────────────────
  // Remote config (per-service tax/fee overrides) is fetched once on app
  // load, but if this page is the first one visited this session it may not
  // have resolved yet — re-fetch (no-op if already done) and force one
  // re-render once it lands, same pattern used by ScheduleSummaryPage.js's
  // per-service fee fix.
  const [configVersion, forceConfigRerender] = useState(0);
  useEffect(() => {
    fetchRemoteConfig().then(() => forceConfigRerender((n) => n + 1));
  }, []);

  const subtotal   = useMemo(() => cartItems.reduce((s,i) => s + Number(i.price||0)*Number(i.qty||1), 0), [cartItems]);
  const totalQty   = cartItems.reduce((s,i) => s+Number(i.qty||1), 0);
  const hasDisabledItems = cartItems.some((i) => !isServiceEnabled(String(i.id)));

  const CHARGES     = CONFIG.CHECKOUT_CHARGES ?? { DISCOUNT_PERCENT: 5, GST_PERCENT: 18, PLATFORM_FEE: 20 };
  const discount    = Math.round(subtotal * (CHARGES.DISCOUNT_PERCENT / 100));
  const taxableAmt  = subtotal - discount;

  // Tax and Platform Fee are per-service (Dashboard's Enable/Disable table
  // lets an admin override either for a specific service; absent = the
  // global CHARGES default above) — same model as ScheduleSummaryPage.js.
  // Tax is summed per cart item on its own proportional share of the
  // order-wide discount; Platform Fee is summed once per DISTINCT service
  // in the cart, not per unit quantity.
  const gst = useMemo(() => {
    if (subtotal <= 0) return 0;
    return Math.round(cartItems.reduce((sum, item) => {
      const itemTotal   = Number(item.price || 0) * Number(item.qty || 1);
      const itemTaxable = itemTotal * (taxableAmt / subtotal);
      const { taxPercent } = getServiceFees(String(item.id));
      return sum + itemTaxable * (taxPercent / 100);
    }, 0));
  }, [cartItems, subtotal, taxableAmt, configVersion]);

  const platformFee = useMemo(() => {
    const seen = new Set();
    let sum = 0;
    cartItems.forEach((item) => {
      const key = String(item.id);
      if (seen.has(key)) return;
      seen.add(key);
      sum += getServiceFees(key).platformFee;
    });
    return sum;
  }, [cartItems, configVersion]);

  const effectiveTaxPercent = taxableAmt > 0 ? Math.round((gst / taxableAmt) * 1000) / 10 : CHARGES.GST_PERCENT;
  const total = taxableAmt + gst + platformFee;

  const handleDeleteService = (itemId) => {
    setCartItems((prev) => {
      const item = prev.find((i) => String(i.id) === String(itemId));
      if (item && userMobile) apiCartRemoveItem(userMobile, item);
      return prev.filter((i) => String(i.id) !== String(itemId));
    });
  };

  // ── Place order — no payment at checkout, straight to the tracking page ──
  const handlePlaceOrder = async () => {
    if (!agreedToTerms || placing || hasDisabledItems || cartItems.length === 0) return;
    if (timingMode === "slot" && !selectedSlot) return;
    if (!userMobile) {
      navigate("/user/user-login", { state:{ redirectTo:"/checkout-instant/summary" } });
      return;
    }

    // Only the first (main) cart item gets the slot mapping — addons ride
    // along on the same visit/seat, they must NOT each independently claim
    // a seat off the same slot.
    const bookingSlots = (timingMode === "slot" && selectedSlot)
      ? [{
          service_id: String(cartItems[0].id),
          date:       formatTodayDDMMYYYY(),
          time:       selectedSlot.time,
          slot_id:    selectedSlot.slotId,
        }]
      : [];

    // Stamp the chosen duration onto the main item so it's on the order
    // record — same schedule_duration field Schedule Booking uses.
    const items = (timingMode === "slot" && selectedSlot)
      ? cartItems.map((item, i) => i === 0 ? { ...item, schedule_duration: selectedDuration } : item)
      : cartItems;

    const orderPayload = {
      mobile:           userMobile,
      customer_name:    userName,
      items,
      total_amount:     total,
      selected_city:    getUserCity(),
      instructions,
      delivery_address: effectiveAddress || null,
      booking_slots:    bookingSlots,
      booking_type:     "instant",
      payment_option:   "pay_after_work",
    };

    setPlacing(true);
    setPlaceError("");
    try {
      const data = await apiPlaceOrder(orderPayload);
      await apiCartClear(userMobile);
      clearLocalCart();
      setCartItemsRaw([]);
      navigate(`/instant-tracking/${data?.data?.order_id || ""}`, { replace:true });
    } catch (err) {
      setPlaceError(err.message || "Failed to place order. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  // ── Empty cart ───────────────────────────────────────────────────────────
  if (cartItems.length === 0) {
    return (
      <div style={S.page}>
        <div style={{ ...S.container, textAlign:"center", paddingTop:"90px" }}>
          <div style={{ fontSize:64 }}>🛒</div>
          <h2 style={{ fontSize:"30px", fontWeight:900, margin:"18px 0 10px" }}>Your cart is empty</h2>
          <p style={{ color:"#64748b", fontSize:"16px", marginBottom:"28px" }}>
            Add a service to continue with an instant booking.
          </p>
          <button style={{ ...S.primaryBtn, display:"inline-block" }} onClick={() => navigate("/")}>
            Browse Services
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.container}>

        {/* Hero */}
        <div style={S.topHero}>
          <div>
            <p style={S.eyebrow}>Instant Checkout</p>
            <h1 style={S.pageTitle}>We'll find a professional right away</h1>
            <p style={S.pageSubtitle}>No payment now — you'll pay once your professional arrives.</p>
          </div>
          <div style={S.payableCard}>
            <div style={S.payableLabel}>Amount payable</div>
            <div style={S.payableValue}>{fmt(total)}</div>
            <div style={S.payableMeta}>{cartItems.length} services · {totalQty} items</div>
            <div style={S.payLaterBadge}>⚡ Pay once professional arrives</div>
          </div>
        </div>

        <div style={S.mainGrid}>

          {/* ── LEFT ── */}
          <div style={S.leftColumn}>

            {/* Timing — Live, or Schedule for a today slot */}
            <section style={S.card}>
              <SectionTitle title="Live or Schedule?" desc="Go live right away, or schedule an open slot later today." />
              <div style={S.bookingTypeRow}>
                <button type="button"
                  style={{ ...S.bookingTypeCard, ...(timingMode === "now" ? S.bookingTypeCardActive : {}) }}
                  onClick={() => handleChooseTimingMode("now")}>
                  <div style={S.bookingTypeIcon}>⚡</div>
                  <div>
                    <div style={S.bookingTypeLabel}>Live</div>
                    <div style={S.bookingTypeSub}>We search for a nearby professional immediately</div>
                  </div>
                  {timingMode === "now" && <div style={S.payOptionCheck}>✓</div>}
                </button>
                <button type="button"
                  style={{ ...S.bookingTypeCard, ...(timingMode === "slot" ? S.bookingTypeCardActive : {}) }}
                  onClick={() => handleChooseTimingMode("slot")}>
                  <div style={S.bookingTypeIcon}>🕐</div>
                  <div>
                    <div style={S.bookingTypeLabel}>Schedule</div>
                    <div style={S.bookingTypeSub}>Pick an open slot today — at least 1 hour from now</div>
                  </div>
                  {timingMode === "slot" && <div style={S.payOptionCheck}>✓</div>}
                </button>
              </div>

              {timingMode === "slot" && (
                <div style={S.slotGridWrap}>
                  <div style={S.durationRow}>
                    {[{ value:1, label:"1 Hour" }, { value:2, label:"2 Hour" }].map((d) => (
                      <button key={d.value} type="button"
                        style={{ ...S.durationBtn, ...(selectedDuration === d.value ? S.durationBtnActive : {}) }}
                        onClick={() => handleChooseDuration(d.value)}>
                        {d.label}
                      </button>
                    ))}
                  </div>
                  {slotLoading ? (
                    <div style={S.addrStateBox}>Loading today's slots…</div>
                  ) : slotError ? (
                    <div style={S.addrStateBox}>{slotError}</div>
                  ) : todaySlots.length === 0 ? (
                    <div style={S.addrStateBox}>No more slots left for today — try Live instead.</div>
                  ) : (
                    <div style={S.slotGrid}>
                      {todaySlots.map((slot) => {
                        const selectable = isTodaySlotSelectable(slot);
                        const isPast     = !selectable && slotTimeToday(slot.time) && slotTimeToday(slot.time).getTime() <= Date.now() + 60*60*1000;
                        const isFull     = !selectable && !isPast;
                        const active     = selectedSlot?.slotId === slot._id;
                        return (
                          <button key={slot._id} type="button"
                            disabled={!selectable}
                            style={{
                              ...S.slotBtn,
                              ...(active ? S.slotBtnActive : {}),
                              ...(!selectable ? S.slotBtnDisabled : {}),
                            }}
                            onClick={() => selectable && setSelectedSlot({ time: slot.time, slotId: slot._id })}>
                            <div>{slot.time}</div>
                            <div style={S.slotBtnSub}>
                              {isPast ? "Too soon" : isFull ? "Full" : `${slot.seats - slot.booked} seats left`}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {selectedSlot && (
                    <div style={S.slotPickedNote}>✓ Booked for today, {selectedSlot.time}</div>
                  )}
                </div>
              )}
            </section>

            {/* Address */}
            <section style={S.card}>
              <SectionTitle title="Service Address" desc="Select where the professional should arrive."
                right={
                  <button style={S.ghostBtn}
                    onClick={() => navigate("/profile-dashboard", { state:{ openAddresses: true } })}>
                    Manage Addresses →
                  </button>
                } />

              <div style={S.addrTabRow}>
                <button type="button" style={{ ...S.addrTab, ...(locationMode === "saved" ? S.addrTabActive : {}) }}
                  onClick={() => setLocationMode("saved")}>
                  📍 Saved Address
                </button>
                <button type="button" style={{ ...S.addrTab, ...(locationMode === "current" ? S.addrTabActive : {}) }}
                  onClick={() => setLocationMode("current")}>
                  🛰 Current Location
                </button>
              </div>

              {locationMode === "saved" ? (
                addrLoading ? (
                  <div style={S.addrStateBox}>Loading your saved addresses…</div>
                ) : savedAddresses.length === 0 ? (
                  <div style={S.addrStateBox}>
                    <div style={{ fontSize:32, marginBottom:8 }}>📍</div>
                    <div style={{ fontWeight:700, marginBottom:6 }}>No saved addresses</div>
                    <div style={{ fontSize:13, color:"#64748b", marginBottom:14 }}>
                      Add up to 3 addresses in your profile to use here.
                    </div>
                    <button style={S.outlineBtn}
                      onClick={() => navigate("/profile-dashboard", { state:{ openAddresses: true } })}>
                      + Add Address in Profile
                    </button>
                  </div>
                ) : (
                  <div style={S.addressLayout}>
                    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                      {savedAddresses.map(addr => {
                        const active = selectedAddrId === addr.id;
                        const fullAddr = [addr.line1, addr.area, addr.city, addr.state, addr.pincode]
                          .filter(Boolean).join(", ");
                        return (
                          <div key={addr.id}
                            style={{ ...S.addressCard, ...(active ? S.addressCardSelected : {}), cursor:"pointer" }}
                            onClick={() => setSelectedAddrId(addr.id)}>
                            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                              <input type="radio" readOnly checked={active} style={{ accentColor:"#4f46e5", width:16, height:16 }} />
                              <span style={{ ...S.addrBadge, ...(active ? { background:"#eef2ff", color:"#4338ca", border:"1px solid #c7d2fe" } : {}) }}>
                                {ADDR_LABEL_ICONS[addr.label] || "📍"} {addr.label || "Address"}
                              </span>
                            </div>
                            <div style={S.addressBlock}>{fullAddr}</div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={S.mapCard}>
                      {selectedAddress && (
                        <GoogleMapPreview addr={selectedAddress} height={200}
                          emptyHint="Add address details in My Addresses" />
                      )}
                    </div>
                  </div>
                )
              ) : (
                <div>
                  {!IS_GOOGLE_CONFIGURED && (
                    <div style={S.testingBadge}>
                      🧪 Testing Mode — no Google Maps API key configured yet. Pick a dummy location below.
                    </div>
                  )}
                  {IS_GOOGLE_CONFIGURED ? (
                    <CurrentLocationDetector onDetected={setCurrentLocationAddr}
                      buttonStyle={S.detectBtn} errorStyle={S.locationErrorText} />
                  ) : (
                    <select style={S.testSelect} value={testLocationId}
                      onChange={(e) => handleSelectTestLocation(e.target.value)}>
                      <option value="">Select a test location…</option>
                      {DUMMY_TEST_LOCATIONS.map(l => (
                        <option key={l.id} value={l.id}>{l.line1}, {l.city}</option>
                      ))}
                    </select>
                  )}
                  {currentLocationAddr && (
                    <div style={{ ...S.addressLayout, marginTop:"14px" }}>
                      <CurrentLocationCard addr={currentLocationAddr} onChange={setCurrentLocationAddr}
                        cardStyle={S.addressCard}
                        badgeStyle={{ ...S.addrBadge, background:"#eef2ff", color:"#4338ca", border:"1px solid #c7d2fe" }}
                        blockStyle={S.addressBlock} />
                      <div style={S.mapCard}>
                        <GoogleMapPreview addr={currentLocationAddr} height={200}
                          emptyTitle="Map preview needs a Google Maps API key" />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Services */}
            <section style={S.card}>
              <SectionTitle title="Services" desc="Dispatched together as one instant booking." />
              <div style={S.serviceSlotList}>
                {cartItems.map((item, index) => (
                  <div key={item.id||index} style={S.serviceSlotCard}>
                    <div style={S.serviceSlotLeft}>
                      {item.image
                        ? <img src={item.image} alt={item.name} style={S.summaryImg} />
                        : <div style={S.summaryImgFallback}>{item.name?.charAt(0)||"S"}</div>
                      }
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={S.summaryItemTop}>
                          <h4 style={S.summaryItemName}>{item.name||item.title}</h4>
                          <div style={S.summaryItemPrice}>{fmt(Number(item.price||0)*Number(item.qty||1))}</div>
                        </div>
                        <div style={S.summaryItemDesc}>{item.short||item.subtitle||"Professional service"}</div>
                      </div>
                    </div>
                    <button style={S.deleteIconBtn} onClick={() => handleDeleteService(item.id)}
                      title={`Delete ${item.name}`}>🗑</button>
                  </div>
                ))}
              </div>
            </section>

            {/* Instructions */}
            <section style={S.card}>
              <SectionTitle title="Additional Instructions" desc="Share any note for the professional." />
              <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)}
                placeholder="E.g. Please call before arrival..." style={S.textarea} />
            </section>

            {/* Footer CTA */}
            <section style={S.footerActionCard}>
              {placeError && <div style={S.errorBanner}>{placeError}</div>}
              {hasDisabledItems && (
                <div style={S.disabledServiceBanner}>
                  ⏸ One or more services in your cart are currently stopped. We will get back to you shortly. Please remove them or check back later.
                </div>
              )}

              <div style={S.instantPayNote}>
                {timingMode === "slot" && selectedSlot
                  ? `🕐 No payment needed now — we'll start finding a professional closer to ${selectedSlot.time} today. You'll pay once they confirm arrival.`
                  : "⚡ No payment needed now — we'll search for a nearby professional first. You'll pay once they confirm arrival at your location."}
              </div>

              <label style={S.termsWrap}>
                <input type="checkbox" checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)} />
                <span>
                  I agree to the{" "}
                  <a href="/legal-information" style={S.inlineLink}>Terms & Conditions</a> and{" "}
                  <a href="/legal-information" style={S.inlineLink}>Privacy Policy</a>
                </span>
              </label>
              <div style={S.footerBtns}>
                <button style={S.secondaryBtn} onClick={() => navigate(-1)}>← Back</button>
                <button
                  style={{ ...S.primaryBtn, ...(!agreedToTerms||placing||hasDisabledItems||(timingMode==="slot"&&!selectedSlot)?S.disabledBtn:{}) }}
                  disabled={!agreedToTerms||placing||hasDisabledItems||(timingMode==="slot"&&!selectedSlot)}
                  onClick={handlePlaceOrder}>
                  {placing
                    ? "Confirming…"
                    : timingMode === "slot"
                      ? (selectedSlot ? `Confirm — Book for ${selectedSlot.time} Today` : "Pick a Slot to Continue")
                      : "Confirm — Search for a Professional"}
                </button>
              </div>
            </section>
          </div>

          {/* ── RIGHT: Summary sidebar ── */}
          <div style={S.rightColumn}>
            <section style={S.summaryCard}>
              {loggedInUser && (
                <div style={S.profileCard}>
                  <div style={S.profileAvatar}>{userName.charAt(0).toUpperCase()}</div>
                  <div>
                    <div style={S.profileLabel}>Booking for</div>
                    <div style={S.profileName}>{userName}</div>
                    {loggedInUser.mobile && <div style={S.profileMeta}>{loggedInUser.mobile}</div>}
                  </div>
                </div>
              )}
              <div style={S.summaryHead}>
                <div>
                  <h3 style={S.summaryTitle}>Booking Summary</h3>
                  <p style={S.summaryMeta}>{cartItems.length} services · {totalQty} items</p>
                  <div style={S.splitPill}>⚡ Pay once professional arrives</div>
                </div>
                <button style={S.ghostBtnSmall}
                  onClick={() => navigate("/profile-dashboard", { state:{ openCart:true } })}>
                  Edit Cart
                </button>
              </div>
              <div style={S.summaryItems}>
                {cartItems.map((item, i) => (
                  <div key={item.id||i} style={S.summaryItem}>
                    {item.image
                      ? <img src={item.image} alt={item.name} style={S.summaryImg} />
                      : <div style={S.summaryImgFallback}>{item.name?.charAt(0)||"S"}</div>
                    }
                    <div style={S.summaryItemBody}>
                      <div style={S.summaryItemTop}>
                        <h4 style={S.summaryItemName}>{item.name||item.title}</h4>
                        <div style={S.summaryItemPrice}>{fmt(Number(item.price||0)*Number(item.qty||1))}</div>
                      </div>
                      <div style={S.summaryItemDesc}>{item.short||item.subtitle}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={S.billBox}>
                <div style={S.billRow}><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
                {discount > 0 && <div style={S.billRow}><span>Discount</span><span style={{ color:"#16a34a" }}>-{fmt(discount)}</span></div>}
                <div style={S.billRow}><span>GST ({effectiveTaxPercent}%)</span><span>{fmt(gst)}</span></div>
                {platformFee > 0 && <div style={S.billRow}><span>Platform Fee</span><span>{fmt(platformFee)}</span></div>}
                <div style={S.billDivider} />
                <div style={S.billTotalRow}><span>Total</span><span>{fmt(total)}</span></div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

const InstantSummaryPage = () => <InstantSummaryPageContent />;

// ─────────────────────────────────────────────────────────────────────────────
// STYLES — mirrors ScheduleSummaryPage.js's tokens for a consistent look.
// ─────────────────────────────────────────────────────────────────────────────
const S = {
  page:      { background:"#f8fafc", minHeight:"100vh", padding:"28px 20px 60px", fontFamily:"Inter,ui-sans-serif,system-ui,sans-serif", color:"#0f172a" },
  container: { maxWidth:"1200px", margin:"0 auto" },

  topHero:      { display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:"20px", marginBottom:"24px", flexWrap:"wrap" },
  eyebrow:      { margin:0, fontSize:"12px", fontWeight:800, color:"#4f46e5", textTransform:"uppercase", letterSpacing:"0.05em" },
  pageTitle:    { margin:"6px 0 4px", fontSize:"26px", fontWeight:900 },
  pageSubtitle: { margin:0, fontSize:"14px", color:"#64748b" },
  payableCard:  { background:"#fff", border:"1px solid #e2e8f0", borderRadius:"18px", padding:"18px 22px", minWidth:"220px" },
  payableLabel: { fontSize:"13px", color:"#64748b", fontWeight:700 },
  payableValue: { marginTop:"6px", fontSize:"32px", fontWeight:900, color:"#312e81" },
  payableMeta:  { marginTop:"4px", fontSize:"13px", color:"#64748b" },
  payLaterBadge:{ marginTop:"10px", padding:"7px 12px", borderRadius:"10px", background:"#fff7ed", border:"1px solid #fdba74", color:"#c2410c", fontSize:"12px", fontWeight:700, textAlign:"center" },

  mainGrid:    { display:"grid", gridTemplateColumns:"minmax(0,1.75fr) minmax(340px,0.95fr)", gap:"24px", alignItems:"start" },
  leftColumn:  { display:"flex", flexDirection:"column", gap:"20px", minWidth:0 },
  rightColumn: { display:"flex", flexDirection:"column", gap:"18px", minWidth:0 },

  card:        { background:"#fff", border:"1px solid #e2e8f0", borderRadius:"18px", padding:"20px" },
  sectionHead: { display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:"12px", marginBottom:"14px" },
  cardTitle:   { margin:0, fontSize:"17px", fontWeight:800 },
  cardDesc:    { margin:"6px 0 0", fontSize:"13px", color:"#64748b", lineHeight:1.5 },
  ghostBtn:      { height:"38px", padding:"0 14px", borderRadius:"10px", border:"1px solid #e2e8f0", background:"#fff", color:"#334155", cursor:"pointer", fontWeight:700, fontSize:"13px" },
  ghostBtnSmall: { height:"34px", padding:"0 12px", borderRadius:"10px", border:"1px solid #e2e8f0", background:"#fff", color:"#334155", cursor:"pointer", fontWeight:700, fontSize:"12px", whiteSpace:"nowrap" },
  outlineBtn:    { width:"fit-content", height:"40px", padding:"0 14px", borderRadius:"10px", border:"1px solid #c7d2fe", background:"#fff", color:"#4f46e5", cursor:"pointer", fontWeight:800, fontSize:"13px" },

  bookingTypeRow:       { display:"flex", gap:"10px", flexWrap:"wrap" },
  bookingTypeCard:      { flex:1, minWidth:"220px", display:"flex", alignItems:"center", gap:"12px", padding:"14px 16px", borderRadius:"14px", border:"1.5px solid #e2e8f0", background:"#fff", cursor:"pointer", textAlign:"left" },
  bookingTypeCardActive:{ border:"2px solid #4f46e5", background:"#eef2ff" },
  bookingTypeIcon:      { fontSize:"24px", flexShrink:0 },
  bookingTypeLabel:     { fontSize:"14px", fontWeight:800, color:"#0f172a" },
  bookingTypeSub:       { fontSize:"12px", color:"#64748b", marginTop:"2px" },
  payOptionCheck:       { width:"22px", height:"22px", borderRadius:"50%", background:"#4f46e5", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"12px", fontWeight:900, flexShrink:0, marginLeft:"auto" },

  slotGridWrap:    { marginTop:"14px" },
  durationRow:     { display:"flex", gap:"10px", marginBottom:"14px" },
  durationBtn:     { padding:"9px 18px", borderRadius:"10px", border:"1.5px solid #e2e8f0", background:"#fff", color:"#334155", cursor:"pointer", fontWeight:800, fontSize:"13px" },
  durationBtnActive:{ border:"2px solid #4f46e5", background:"#eef2ff", color:"#3730a3" },
  slotGrid:        { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(120px, 1fr))", gap:"10px" },
  slotBtn:         { display:"flex", flexDirection:"column", alignItems:"center", gap:"4px", padding:"12px 10px", borderRadius:"12px", border:"1.5px solid #e2e8f0", background:"#fff", cursor:"pointer", fontSize:"13px", fontWeight:800, color:"#0f172a" },
  slotBtnActive:   { border:"2px solid #4f46e5", background:"#eef2ff", color:"#3730a3" },
  slotBtnDisabled: { opacity:0.45, cursor:"not-allowed", background:"#f8fafc" },
  slotBtnSub:      { fontSize:"11px", fontWeight:600, color:"#64748b" },
  slotPickedNote:  { marginTop:"12px", fontSize:"13px", fontWeight:700, color:"#15803d" },

  addrTabRow:   { display:"flex", gap:"8px", marginBottom:"16px" },
  addrTab:      { height:"38px", padding:"0 16px", borderRadius:"10px", border:"1px solid #e2e8f0", background:"#fff", color:"#475569", cursor:"pointer", fontWeight:700, fontSize:"13px" },
  addrTabActive:{ border:"1px solid #4f46e5", background:"#eef2ff", color:"#4338ca" },
  addrStateBox: { borderRadius:"14px", border:"1px solid #e2e8f0", background:"#f8fafc", padding:"28px 20px", textAlign:"center", fontSize:"14px", color:"#475569" },
  addressLayout:{ display:"grid", gridTemplateColumns:"minmax(260px,0.9fr) minmax(0,1.1fr)", gap:"16px" },
  addressCard:        { border:"1px solid #e2e8f0", borderRadius:"16px", padding:"16px", background:"#fbfdff", display:"flex", flexDirection:"column", gap:"12px", transition:"border 0.15s" },
  addressCardSelected:{ border:"2px solid #4f46e5", background:"#fafbff" },
  addrBadge:          { width:"fit-content", padding:"5px 10px", borderRadius:"999px", background:"#eef2ff", color:"#4338ca", fontSize:"13px", fontWeight:800 },
  addressBlock:       { fontSize:"14px", color:"#334155", lineHeight:1.8 },
  mapCard:  { border:"1px solid #e2e8f0", borderRadius:"16px", overflow:"hidden", minHeight:"200px", background:"#f8fafc" },
  mapImage: { width:"100%", height:"100%", objectFit:"cover", display:"block" },
  testingBadge:      { background:"#fffbeb", border:"1px solid #fde68a", color:"#92400e", fontSize:"12.5px", lineHeight:1.6, borderRadius:"12px", padding:"12px 14px", marginBottom:"14px" },
  detectBtn:         { height:"44px", padding:"0 18px", borderRadius:"12px", border:"1.5px solid #4f46e5", background:"#eef2ff", color:"#4338ca", fontWeight:800, fontSize:"13.5px", cursor:"pointer" },
  locationErrorText: { fontSize:"12.5px", color:"#dc2626", marginTop:"10px" },
  testSelect:        { width:"100%", maxWidth:"360px", height:"44px", padding:"0 14px", borderRadius:"12px", border:"1.5px solid #e2e8f0", background:"#fff", color:"#111827", fontSize:"13.5px" },

  serviceSlotList: { display:"flex", flexDirection:"column", gap:"12px" },
  serviceSlotCard: { border:"1px solid #e2e8f0", borderRadius:"16px", padding:"14px", background:"#fbfdff", display:"flex", justifyContent:"space-between", gap:"14px", alignItems:"center", flexWrap:"wrap" },
  serviceSlotLeft: { display:"flex", gap:"14px", alignItems:"flex-start", flex:1, minWidth:"200px" },
  summaryImg:         { width:"52px", height:"52px", borderRadius:"12px", objectFit:"cover", flexShrink:0 },
  summaryImgFallback: { width:"52px", height:"52px", borderRadius:"12px", background:"#eef2ff", color:"#4338ca", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, flexShrink:0 },
  summaryItemTop:  { display:"flex", justifyContent:"space-between", gap:"10px" },
  summaryItemName: { margin:0, fontSize:"14px", fontWeight:800 },
  summaryItemPrice:{ fontSize:"14px", fontWeight:800, color:"#312e81", whiteSpace:"nowrap" },
  summaryItemDesc: { fontSize:"12px", color:"#64748b", marginTop:"4px" },
  deleteIconBtn:   { width:"36px", height:"36px", borderRadius:"10px", border:"1px solid #fecaca", background:"#fef2f2", color:"#dc2626", cursor:"pointer", fontSize:"14px" },

  textarea: { width:"100%", minHeight:"90px", padding:"12px 14px", borderRadius:"12px", border:"1.5px solid #e2e8f0", fontSize:"14px", resize:"vertical", boxSizing:"border-box", fontFamily:"inherit" },

  footerActionCard: { background:"#fff", border:"1px solid #e2e8f0", borderRadius:"18px", padding:"20px" },
  errorBanner:       { background:"#fef2f2", border:"1px solid #fecaca", color:"#dc2626", fontSize:"13px", padding:"12px 14px", borderRadius:"10px", marginBottom:"14px" },
  disabledServiceBanner: { background:"#fffbeb", border:"1px solid #fde68a", color:"#92400e", fontSize:"13px", padding:"12px 14px", borderRadius:"10px", marginBottom:"14px" },
  instantPayNote: { background:"#eef2ff", border:"1px solid #c7d2fe", color:"#3730a3", fontSize:"13px", lineHeight:1.6, borderRadius:"12px", padding:"12px 14px", marginBottom:"16px" },
  termsWrap:  { display:"flex", alignItems:"flex-start", gap:"8px", fontSize:"12px", color:"#64748b", lineHeight:1.5, marginBottom:"16px" },
  inlineLink: { color:"#4f46e5", fontWeight:700, textDecoration:"none" },
  footerBtns: { display:"flex", gap:"12px" },
  secondaryBtn: { flex:"0 0 auto", padding:"0 22px", height:"50px", borderRadius:"12px", border:"1.5px solid #e2e8f0", background:"#fff", color:"#334155", fontWeight:800, fontSize:"14px", cursor:"pointer" },
  primaryBtn:   { flex:1, height:"50px", borderRadius:"12px", border:"none", background:"linear-gradient(90deg,#4f46e5,#4338ca)", color:"#fff", fontSize:"15px", fontWeight:800, cursor:"pointer" },
  disabledBtn:  { opacity:0.45, cursor:"not-allowed" },

  summaryCard: { background:"#fff", border:"1px solid #e2e8f0", borderRadius:"18px", padding:"20px", position:"sticky", top:"20px" },
  profileCard: { display:"flex", alignItems:"center", gap:"12px", background:"#f8fafc", border:"1px solid #eef2f7", borderRadius:"14px", padding:"12px 14px", marginBottom:"16px" },
  profileAvatar:{ width:"38px", height:"38px", borderRadius:"50%", background:"#4f46e5", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, flexShrink:0 },
  profileLabel: { fontSize:"11px", color:"#94a3b8", fontWeight:700 },
  profileName:  { fontSize:"14px", fontWeight:800 },
  profileMeta:  { fontSize:"12px", color:"#64748b" },
  summaryHead: { display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:"10px", marginBottom:"14px" },
  summaryTitle:{ margin:0, fontSize:"16px", fontWeight:800 },
  summaryMeta: { margin:"4px 0 8px", fontSize:"12px", color:"#64748b" },
  splitPill:   { display:"inline-block", padding:"3px 10px", borderRadius:"999px", background:"#fff7ed", border:"1px solid #fed7aa", color:"#c2410c", fontSize:"12px", fontWeight:700 },
  summaryItems:{ display:"flex", flexDirection:"column", gap:"10px", marginBottom:"16px" },
  summaryItem: { display:"flex", gap:"10px", alignItems:"flex-start" },
  summaryItemBody: { flex:1, minWidth:0 },
  billBox: { borderTop:"1px solid #eef2f7", paddingTop:"14px" },
  billRow: { display:"flex", justifyContent:"space-between", fontSize:"13px", color:"#334155", marginBottom:"8px" },
  billDivider: { borderTop:"1px solid #eef2f7", margin:"8px 0" },
  billTotalRow:{ display:"flex", justifyContent:"space-between", fontSize:"17px", fontWeight:900, color:"#0f172a" },
};

export default InstantSummaryPage;
